import { useCallback } from 'react';
import { useIdleTimeout } from '@/hooks/use-idle-timeout';
import { useAuthStore } from '@/stores/auth-store';
import { usePortalSettings } from '@/hooks/use-settings';
import { IdleTimeoutOverlay } from './idle-timeout-overlay';

const DEFAULT_TIMEOUT_MINUTES = 60;
const WARNING_SECONDS = 60;

/**
 * Monitors user activity and triggers a warning overlay before auto-logout.
 * Reads the session timeout from portal settings, falling back to 60 minutes.
 * Only activates when the user is authenticated.
 */
export function IdleTimeoutMonitor() {
  const { isAuthenticated, logout } = useAuthStore();
  const { data: settingsData } = usePortalSettings();

  const settings = settingsData?.data || {};
  const timeoutMinutes = settings.sessionTimeout
    ? parseInt(settings.sessionTimeout, 10)
    : DEFAULT_TIMEOUT_MINUTES;

  // Ensure a reasonable minimum
  const effectiveTimeout = Math.max(timeoutMinutes, 5);

  const handleTimeout = useCallback(() => {
    logout();
  }, [logout]);

  const { isWarning, remainingSeconds, resetTimer } = useIdleTimeout({
    timeoutMinutes: effectiveTimeout,
    warningSeconds: WARNING_SECONDS,
    onTimeout: handleTimeout,
  });

  // Don't render anything if not authenticated or not in warning state
  if (!isAuthenticated || !isWarning) return null;

  return (
    <IdleTimeoutOverlay
      remainingSeconds={remainingSeconds}
      onStayLoggedIn={resetTimer}
    />
  );
}
